import { ElementType, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
//
import { CSS_EASE, DURATION } from '../motion';

// ----------------------------------------------------------------------

type Props = {
  delay?: number;
  y?: number;
  component?: ElementType;
  children: ReactNode;
};

type Phase = 'scroll' | 'wait' | 'show';

const FIRST_SCREEN_DELAY = 1200;

export function useRevealed(ref: RefObject<Element>) {
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' });

  const [phase, setPhase] = useState<Phase>('scroll');

  useEffect(() => {
    const el = ref.current;
    if (!el || el.getBoundingClientRect().top >= window.innerHeight) {
      return undefined;
    }
    setPhase('wait');
    const timer = window.setTimeout(() => setPhase('show'), FIRST_SCREEN_DELAY);
    return () => window.clearTimeout(timer);
  }, [ref]);

  return phase === 'scroll' ? inView : phase === 'show';
}

export default function Reveal({ delay = 0, y, component = 'div', children }: Props) {
  const ref = useRef<HTMLElement>(null);

  const revealed = useRevealed(ref);

  return (
    <Box
      ref={ref}
      component={component}
      data-enter
      className={revealed ? 'is-revealed' : undefined}
      sx={{
        opacity: 0,
        transform:
          y === undefined
            ? { xs: 'translateY(16px)', sm: 'translateY(24px)' }
            : `translateY(${y}px)`,
        transition: `opacity ${DURATION.reveal}ms ${CSS_EASE.outExpo} ${delay}ms, transform ${DURATION.reveal}ms ${CSS_EASE.outExpo} ${delay}ms`,
        '&.is-revealed, &:focus-within': { opacity: 1, transform: 'none' },
      }}
    >
      {children}
    </Box>
  );
}
